import { PaginationState, SortingState, Updater } from "@tanstack/react-table";
import { useMemo } from "react";

import { useIsMobile } from "../../../hooks";
import { TableDesktop, TableMobile } from "../../../Table";
import { ReturnedTranslatedContentClientsPageHook } from "../../types";
import { useClientApprovedNotFundedContext } from "../providers/ApprovedNotFundedProvider";
import { clientApprovedNotFundedColumns } from "./Columns";
import { MobileCardBody, MobileCardHeader } from "./MobileCard";

export type ClientApprovedNotFundedTableProps = {
  isBackoffice?: boolean;
  translations: ReturnedTranslatedContentClientsPageHook;
};

export const ClientApprovedNotFundedTable = ({
  translations,
  isBackoffice = false,
}: ClientApprovedNotFundedTableProps) => {
  const { translatedContent, language } = translations;
  const {
    state: { page, pageSize, sortBy, sortOrder },
    resources: { table },
    actions: { setPage, setSort },
    runResource,
  } = useClientApprovedNotFundedContext();

  const { error } = table;
  const { list = [], totalCount } = table.data ?? {};
  const isLoading = table.status === "loading";

  const isMobile = useIsMobile();

  const sorting = useMemo(
    () =>
      sortBy != null && sortOrder != null
        ? [{ desc: sortOrder === "desc", id: sortBy }]
        : [],
    [sortOrder, sortBy],
  );

  const tableConfig = useMemo(
    () => ({
      translatedContent:
        translatedContent.approvedNotFundedTab.approvedNotFundedColumnLabels,
      language,
    }),
    [translatedContent, language],
  );

  const columns = clientApprovedNotFundedColumns({
    ...tableConfig,
    isBackoffice,
  });

  const handlePaginationChange = (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updater === "function"
        ? updater({
            pageIndex: page,
            pageSize,
          })
        : updater;

    setPage(newPagination.pageIndex);
  };

  const handleSortingUpdate = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;
    const newSort = nextSorting[0];
    if (newSort.id !== sortBy) {
      setSort?.(newSort.id, "desc");
    } else {
      setSort?.(nextSorting[0].id, nextSorting[0].desc ? "desc" : "asc");
    }
  };

  const pageCount = Math.ceil((totalCount ?? 0) / (pageSize ?? 10));

  if (isMobile) {
    return (
      <TableMobile
        data={list}
        onRetry={() => runResource("table")}
        isError={Boolean(error)}
        columns={columns}
        cardHeader={MobileCardHeader(tableConfig)}
        cardBody={MobileCardBody(tableConfig)}
        showPagination
        translatedContent={translatedContent}
        filterFns={{} as any}
        isLoading={isLoading}
        enableServerSidePagination={true}
        pageCount={pageCount}
        totalCount={totalCount}
        state={{ pagination: { pageIndex: page, pageSize } }}
        onPaginationChange={handlePaginationChange}
      />
    );
  }

  return (
    <TableDesktop
      data={list}
      onRetry={() => runResource("table")}
      isError={Boolean(error)}
      columns={columns}
      showPagination
      translatedContent={translatedContent}
      enableSorting
      enabledServerSideSorting
      sorting={sorting}
      onSortingChange={handleSortingUpdate}
      pageSize={pageSize}
      isLoading={isLoading}
      filterFns={{} as any}
      enableServerSidePagination={true}
      pageCount={pageCount}
      totalCount={totalCount}
      state={{ pagination: { pageIndex: page, pageSize } }}
      onPaginationChange={handlePaginationChange}
    />
  );
};
